import {
  Component,
  ViewChild,
  ViewEncapsulation,
  input,
  ChangeDetectionStrategy,
} from '@angular/core';
import { environment } from '@root/environments/environment';
import {
  DxReportViewerComponent,
  DxReportViewerModule,
} from 'devexpress-reporting-angular';
import { ActionId } from 'devexpress-reporting/dx-webdocumentviewer';

@Component({
  selector: 'report-viewer',
  encapsulation: ViewEncapsulation.None,
  templateUrl: './report-viewer.component.html',
  styleUrls: [
    // ...
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [DxReportViewerModule],
})
export class ReportViewerComponent {
  @ViewChild(DxReportViewerComponent, { static: false })
  viewer: DxReportViewerComponent;

  reportId = input('reportId');
  fileName = input<string>(null);

  apiUrl = `${environment.apiUrl}`;
  // The built-in controller in the back-end ASP.NET Core Reporting application.
  invokeAction: string = '/DXXRDV';

  hostUrl = `${this.apiUrl}/dex-wrapper`;
  private exportFileNamePatched = false;

  onBeforeRender(event: any): void {
    const preview =
      event?.sender?.GetReportPreview?.() ?? event?.args?.reportPreview;
    if (preview) {
      preview.showMultipagePreview = true;
      preview.autoFitBy = 1;
      preview.zoom = 1;
      this.patchExportFileNameResolver(preview);
    }
  }

  CustomizeMenuActions(event) {
    // Hide the "Print" and "PrintPage" actions.
    var printAction = event.args.GetById(ActionId.Print);
    if (printAction) printAction.visible = true;
    var printPageAction = event.args.GetById(ActionId.PrintPage);
    if (printPageAction) printPageAction.visible = false;
    var exportAction = event.args.GetById(ActionId.ExportTo);
    if (exportAction) {
      exportAction.imageClassName = 'dxrd-image-save';
      exportAction.imageTemplateName = 'dxrd-svg-menu-save';
    }
  }

  CustomizeExportOptions(event) {
    const disabledOptions = [
      'csv',
      'html',
      'mht',
      'rtf',
      'textExportOptions',
      'xls',
      'xlsx',
      'docx',
      'image',
    ];
    for (const option of disabledOptions) {
      delete event.args._options.exportOptions[option];
    }

    this.setDefaultExportFileName(event.args?._options?.exportOptions);
  }

  OnExport(event: any) {
    const args = event?.args ?? event;

    if (args?.FormData?.arg) {
      args.FormData.arg = this.patchExportRequestArg(args.FormData.arg);
    }

    if (args?.QueryParameters?.arg) {
      args.QueryParameters.arg = this.patchExportRequestArg(
        args.QueryParameters.arg
      );
    }
  }

  private patchExportRequestArg(arg: string): string {
    try {
      const parsedArg = JSON.parse(arg);
      parsedArg.exportOptions ??= {};
      parsedArg.exportOptions.PrintPreview ??= {};
      parsedArg.exportOptions.PrintPreview['@DefaultFileName'] =
        this.getExportFileName();

      return JSON.stringify(parsedArg);
    } catch {
      return arg;
    }
  }

  private setDefaultExportFileName(exportOptions: any): void {
    if (!exportOptions) {
      return;
    }

    exportOptions.printPreview ??= {};
    exportOptions.printPreview.defaultFileName = this.getExportFileName();
  }

  private getExportFileName(): string {
    const fallbackName = this.reportId() || 'report';
    const fileName = (this.fileName() || fallbackName).trim();

    return (
      fileName
        .replace(/[<>:"/\\|?*\u0000-\u001f]/g, '-')
        .replace(/\s+/g, ' ')
        .replace(/\.+$/g, '')
        .trim() || fallbackName
    );
  }

  private patchExportFileNameResolver(preview: any): void {
    if (this.exportFileNamePatched || !preview?.exportHandler?._getFileName) {
      return;
    }

    const exportHandler = preview.exportHandler;
    const originalGetFileName = exportHandler._getFileName.bind(exportHandler);

    exportHandler._getFileName = (contentDisposition: string) => {
      const originalFileName = originalGetFileName(contentDisposition);
      const extension = this.getFileExtension(originalFileName);
      return `${this.getExportFileName()}${extension}`;
    };

    this.exportFileNamePatched = true;
  }

  private getFileExtension(fileName: string): string {
    const lastDotIndex = fileName?.lastIndexOf('.') ?? -1;
    if (lastDotIndex < 0) {
      return '';
    }

    return fileName.slice(lastDotIndex);
  }
}
